"use client";
import { useCompositeScopedContext } from "./composite-context.js";
import { createElement, createHook, forwardRef, memo, useBooleanEvent, useEvent, useIsMouseMoving, useMergeRefs } from "@ariakit/react-utils";
import { contains, hasFocus, hasFocusWithin, hasOwnProperty, invariant, isElement, removeUndefinedValues } from "@ariakit/utils";
import { useCallback } from "react";
//#region src/composite/composite-hover.tsx
const TagName = "div";
function hoveringInside(event) {
	const nextElement = event.relatedTarget;
	if (!isElement(nextElement)) return false;
	return contains(event.currentTarget, nextElement);
}
const symbol = Symbol("composite-hover");
function movingToAnotherItem(event) {
	const { relatedTarget } = event;
	if (!isElement(relatedTarget)) return false;
	let dest = relatedTarget;
	do {
		if (hasOwnProperty(dest, symbol) && dest[symbol]) return true;
		dest = dest.parentElement;
	} while (dest);
	return false;
}
/**
* Returns props to create a `CompositeHover` component. The composite item that
* receives these props will get focused on mouse move and lose focus to the
* composite base element on mouse leave. This should be combined with the
* `CompositeItem` component, the `useCompositeItem` hook or any component/hook
* that uses them underneath.
* @see https://ariakit.com/components/composite
* @example
* ```jsx
* const store = useCompositeStore();
* const props = useCompositeHover({ store });
* <CompositeItem store={store} {...props}>Item</CompositeItem>
* ```
*/
const useCompositeHover = createHook(function useCompositeHover({ store, focusOnHover = true, blurOnHoverEnd = !!focusOnHover, ...props }) {
	const context = useCompositeScopedContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "CompositeHover must be wrapped in a Composite component.");
	const isMouseMoving = useIsMouseMoving();
	const onMouseMoveProp = props.onMouseMove;
	const focusOnHoverProp = useBooleanEvent(focusOnHover);
	const onMouseMove = useEvent((event) => {
		onMouseMoveProp?.(event);
		if (event.defaultPrevented) return;
		if (!isMouseMoving()) return;
		if (!focusOnHoverProp(event)) return;
		if (!hasFocusWithin(event.currentTarget)) {
			const baseElement = store?.getState().baseElement;
			if (baseElement && !hasFocus(baseElement)) baseElement.focus();
		}
		store?.setActiveId(event.currentTarget.id);
	});
	const onMouseLeaveProp = props.onMouseLeave;
	const blurOnHoverEndProp = useBooleanEvent(blurOnHoverEnd);
	const onMouseLeave = useEvent((event) => {
		onMouseLeaveProp?.(event);
		if (event.defaultPrevented) return;
		if (!isMouseMoving()) return;
		if (hoveringInside(event)) return;
		if (movingToAnotherItem(event)) return;
		if (!focusOnHoverProp(event)) return;
		if (!blurOnHoverEndProp(event)) return;
		store?.setActiveId(null);
		store?.getState().baseElement?.focus();
	});
	const ref = useCallback((element) => {
		if (!element) return;
		element[symbol] = true;
	}, []);
	props = {
		...props,
		ref: useMergeRefs(ref, props.ref),
		onMouseMove,
		onMouseLeave
	};
	return removeUndefinedValues(props);
});
/**
* Renders an element in a composite widget that receives focus on mouse move
* and loses focus to the composite base element on mouse leave.
*
* This should be combined with the
* [`CompositeItem`](https://ariakit.com/reference/composite-item) component.
* The
* [`focusOnHover`](https://ariakit.com/reference/composite-hover#focusonhover)
* and
* [`blurOnHoverEnd`](https://ariakit.com/reference/composite-hover#bluronhoverend)
* props can be used to customize the behavior.
* @see https://ariakit.com/components/composite
* @example
* ```jsx {3-5}
* <CompositeProvider>
*   <Composite>
*     <CompositeHover render={<CompositeItem />}>
*       Item
*     </CompositeHover>
*   </Composite>
* </CompositeProvider>
* ```
*/
const CompositeHover = memo(forwardRef(function CompositeHover(props) {
	return createElement(TagName, useCompositeHover(props));
}));
//#endregion
export { CompositeHover, useCompositeHover };

//# sourceMappingURL=composite-hover.js.map